import { Core } from 'chessboard/core/config/types';
import { createContext } from 'chessboard/core/create-context';
import { createUpdateMachineState } from 'chessboard/core/state';
import type { Modes } from 'chessboard/modes/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import type { Renderer } from 'chessboard/renderers/config/types';
import { runBeforeState } from 'chessboard/core/plugins/run-before-state';
import { runPlugin } from 'chessboard/core/plugins/run-plugin';
import { splitPluginsByHookType } from 'core/model/split-plugins-by-hook-type';
import { Logger } from 'api/logger/config/types';

/**
 * Create the core of the chessboard.
 */
export function createCore({ params, plugins }: Core.CreateParams): Core.API {
  const context = createContext({ params, run });
  const updateMachineState = createUpdateMachineState();

  return {
    getContext,
    getMode,
    run,
    setMode,
    setRenderer,
  };

  /**
   * Get the context object.
   */
  function getContext() {
    return context;
  }

  /**
   * Get the current mode.
   */
  function getMode() {
    return context.mode;
  }

  /**
   * Run the event. Creates a new context each time, and flushes any function calls
   * that have been queued during the run.
   */
  function run(event: Core.Event): void {
    const {
      api: { logger },
    } = context;

    logger.log({
      code: Core.LogCodes.Event,
      data: event,
    });

    context.reset();

    const currentPlugins = plugins.get();

    /**
     * Should not update state if any one `beforeState` hook prohibits it.
     */
    const shouldUpdateState = runBeforeStateHooks(event, currentPlugins);

    if (shouldUpdateState) {
      updateMachineState(event, context);
    }

    const { beforeRender, afterRender } = splitPluginsByHookType(currentPlugins);

    try {
      beforeRender.forEach((plugin) => runPlugin(plugin, [event, context]));

      context.queue.flush();

      afterRender.forEach((plugin) => runPlugin(plugin, [event, context]));

      context.queue.flush();

      logger.log({
        code: Core.LogCodes.State,
        data: context.state.get(),
      });
    } catch (e) {
      logger.log({
        code: Core.LogCodes.State,
        data: {
          // @ts-ignore https://chesscom.atlassian.net/browse/CV-224938
          ...e.data,
          event,
          queue: context.queue,
          state: context.state.get(),
        },
        level: Logger.Levels.Error,
      });

      throw e;
    }
  }

  /**
   * Set the mode.
   */
  function setMode(mode: Modes.API.Extended): void {
    context.setMode(mode);
  }

  /**
   * Set the renderer.
   */
  function setRenderer(renderer?: Renderer.Model): void {
    context.setRenderer(renderer);
  }

  /**
   * Run `beforeState` plugin hooks for a plugin. If any of the beforeState hooks
   * return false, then the state should not be updated for this event.
   */
  function runBeforeStateHooks(event: Core.Event, currentPlugins: Plugins.Model[]): boolean {
    return currentPlugins.every((plugin) => runBeforeState(plugin, [event, context]));
  }
}
